/**
 * @typedef {Object} CatConfig
 * @property {number} walkSpeed - Walking speed
 * @property {number} runSpeed - Running speed (when Caps Lock is on)
 * @property {number} turnSpeed - Rotation speed in radians per second
 * @property {number} jumpVelocity - Initial jump velocity
 * @property {number} gravity - Gravity acceleration
 * @property {number} groundHeight - Height above ground level
 * @property {number} collisionRadius - Collision radius for physics
 */

/**
 * @typedef {Object} CowConfig
 * @property {number} count - Number of cows to spawn
 * @property {number} minSpeed - Minimum wandering speed
 * @property {number} maxSpeed - Maximum wandering speed
 * @property {number} collisionRadius - Collision radius for physics
 * @property {number} turnInterval - Time between random direction changes
 */

/**
 * @typedef {Object} WorldConfig
 * @property {number} terrainSize - Size of the terrain plane
 * @property {number} boundary - Maximum distance from center before boundary
 * @property {number} treeCount - Number of trees to generate
 * @property {number} houseCount - Number of houses to generate
 * @property {number} cloudCount - Number of clouds to generate
 * @property {number} minObjectDistance - Minimum distance between objects
 * @property {number} minHouseDistance - Minimum distance between houses
 */

/**
 * @typedef {Object} CameraOffset
 * @property {number} x - X offset from cat
 * @property {number} y - Y offset from cat
 * @property {number} z - Z offset from cat
 */

/**
 * @typedef {Object} CameraConfig
 * @property {CameraOffset} defaultOffset - Default third-person camera offset
 * @property {CameraOffset} aimOffset - Aim mode camera offset
 * @property {number} maxPitch - Maximum pitch angle in radians
 * @property {number} minPitch - Minimum pitch angle in radians
 * @property {number} lerpFactor - Smoothing factor for camera movement
 * @property {number} houseCollisionDistance - Distance at which camera avoids houses
 */

/**
 * @typedef {Object} ScoringConfig
 * @property {number} treeScratch - Points for scratching a tree
 * @property {number} doorstepPoop - Points for pooping on a doorstep
 * @property {number} cowRoast - Points for igniting a cow
 * @property {number} treeBurn - Points for igniting a tree
 * @property {number} houseBurn - Points for igniting a house
 */

/**
 * @typedef {Object} ShadowConfig
 * @property {number} mapSize - Shadow map resolution
 * @property {number} bias - Shadow bias to reduce shadow acne
 * @property {number} normalBias - Normal bias for curved surfaces
 * @property {number} radius - Shadow blur radius
 */

/**
 * @typedef {Object} SkyConfig
 * @property {number} turbidity - Haziness of atmosphere (2-10)
 * @property {number} rayleigh - Rayleigh scattering coefficient
 * @property {number} mieCoefficient - Mie scattering coefficient
 * @property {number} mieDirectionalG - Mie scattering directionality
 * @property {number} elevation - Sun elevation in degrees (0-90)
 * @property {number} azimuth - Sun azimuth in degrees (0-360)
 */

/**
 * @typedef {Object} LightingConfig
 * @property {number} hemisphereSkyColor - Sky color for hemisphere light (hex)
 * @property {number} hemisphereGroundColor - Ground color for hemisphere light (hex)
 * @property {number} hemisphereIntensity - Intensity of hemisphere light
 * @property {number} directionalIntensity - Intensity of directional (sun) light
 * @property {number} directionalColor - Color of directional light (hex)
 */

/**
 * @typedef {Object} CloudConfig
 * @property {number} puffCountMin - Minimum cloud puffs per cloud
 * @property {number} puffCountMax - Maximum cloud puffs per cloud
 * @property {number} puffSizeMin - Minimum puff size
 * @property {number} puffSizeMax - Maximum puff size
 * @property {number} opacityMin - Minimum cloud opacity
 * @property {number} opacityMax - Maximum cloud opacity
 * @property {number} heightMin - Minimum cloud height
 * @property {number} heightMax - Maximum cloud height
 * @property {number} scaleMin - Minimum cloud scale
 * @property {number} scaleMax - Maximum cloud scale
 * @property {number} driftSpeed - Cloud drift speed
 */

/**
 * @typedef {Object} GameConfig
 * @property {CatConfig} cat - Cat configuration
 * @property {CowConfig} cow - Cow configuration
 * @property {WorldConfig} world - World generation configuration
 * @property {CameraConfig} camera - Camera configuration
 * @property {ScoringConfig} scoring - Scoring configuration
 * @property {ShadowConfig} shadow - Shadow configuration
 * @property {SkyConfig} sky - Sky configuration
 * @property {LightingConfig} lighting - Lighting configuration
 * @property {CloudConfig} cloud - Cloud configuration
 */

/** @type {Readonly<GameConfig>} */
export const CONFIG = Object.freeze({
    cat: {
        walkSpeed: 0.06,
        runSpeed: 0.12,
        turnSpeed: 2.0,
        jumpVelocity: 0.15,
        gravity: 0.007,
        groundHeight: 0.85,
        collisionRadius: 0.6,
    },
    cow: {
        count: 12,
        minSpeed: 0.01,
        maxSpeed: 0.03,
        collisionRadius: 2.0,
        turnInterval: 5.0,
    },
    world: {
        terrainSize: 200,
        boundary: 98,
        treeCount: 25,
        houseCount: 8,
        cloudCount: 10,
        minObjectDistance: 15,
        minHouseDistance: 30,
    },
    camera: {
        defaultOffset: { x: 0, y: 3, z: 6 },
        aimOffset: { x: 0, y: 12, z: -5 },
        maxPitch: Math.PI / 4,
        minPitch: -Math.PI / 6,
        lerpFactor: 0.1,
        houseCollisionDistance: 7,
    },
    scoring: {
        treeScratch: 10,
        doorstepPoop: 20,
        cowRoast: 10,
        treeBurn: 5,
        houseBurn: 15,
    },
    shadow: {
        mapSize: 4096,
        bias: -0.0003,
        normalBias: 0.05,
        radius: 3,
    },
    sky: {
        turbidity: 1.0,
        rayleigh: 2.8,
        mieCoefficient: 0.003,
        mieDirectionalG: 0.85,
        elevation: 55,
        azimuth: 180,
    },
    lighting: {
        hemisphereSkyColor: 0x87CEEB,
        hemisphereGroundColor: 0x4a7c4a,
        hemisphereIntensity: 1.0,
        directionalIntensity: 1.8,
        directionalColor: 0xFFFAF0,
    },
    cloud: {
        puffCountMin: 28,
        puffCountMax: 45,
        puffSizeMin: 3,
        puffSizeMax: 7,
        opacityMin: 0.7,
        opacityMax: 1.0,
        heightMin: 30,
        heightMax: 60,
        scaleMin: 0.8,
        scaleMax: 1.5,
        driftSpeed: 0.5,
    },
});

// House colors for variety
export const HOUSE_COLORS = Object.freeze([
    0xE8BEAC, // Beige
    0xDEB887, // Light brown
    0xF5F5DC, // Off-white
    0xD3D3D3, // Light gray
    0xFFB6C1, // Light pink
    0x020652, // Light blue
    0xE6E6FA  // Lavender
]);

// Flower colors for gardens
export const FLOWER_COLORS = Object.freeze([
    0xFF0000, // Red
    0xFFFF00, // Yellow
    0xFF69B4, // Pink
    0x9932CC, // Purple
    0xFFA500  // Orange
]);
